import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Heart, Users, Award, ThumbsUp, Share2, Gift,
  Briefcase, Shield, GraduationCap, Home, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NominateNeighbor = () => {
  const { token, isAuthenticated, user } = useAuth();
  const [nominations, setNominations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const [formData, setFormData] = useState({
    nominee_name: '',
    nominee_email: '',
    nominee_phone: '',
    nominee_address: '',
    city: '',
    reason: '',
    category: ''
  });

  const categories = [
    { id: 'teacher', name: 'Teacher', icon: GraduationCap, desc: 'Nominate a teacher who goes above and beyond' },
    { id: 'nurse', name: 'Healthcare Hero', icon: Heart, desc: 'Honor a nurse or healthcare worker' },
    { id: 'veteran', name: 'Veteran', icon: Shield, desc: 'Recognize a veteran for their service' },
    { id: 'family_in_need', name: 'Family in Need', icon: Home, desc: 'Help a family facing difficult times' },
    { id: 'neighbor', name: 'Amazing Neighbor', icon: Users, desc: 'Celebrate someone who makes your community better' }
  ];

  useEffect(() => {
    fetchNominations();
  }, [selectedCategory]);

  const fetchNominations = async () => {
    try {
      let url = `${API}/nominations?status=approved&limit=20`;
      if (selectedCategory !== 'all') {
        url += `&category=${selectedCategory}`;
      }
      const response = await axios.get(url);
      setNominations(response.data.nominations || []);
    } catch (error) {
      console.error('Error fetching nominations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Please log in to nominate someone');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/nominations`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Nomination submitted! Thank you for recognizing someone special.');
      setShowForm(false);
      setFormData({
        nominee_name: '',
        nominee_email: '',
        nominee_phone: '',
        nominee_address: '',
        city: '',
        reason: '',
        category: ''
      });
      fetchNominations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (nominationId) => {
    if (!isAuthenticated) {
      toast.error('Please log in to vote');
      return;
    }

    try {
      await axios.post(`${API}/nominations/${nominationId}/vote`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vote recorded!');
      fetchNominations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Already voted');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#FF3B30] to-[#DC2626] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-10" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-white/20 text-white font-medium px-4 py-2 rounded-full text-sm mb-6">
              <Heart className="w-4 h-4" />
              Community Campaign
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              Nominate a Neighbor
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
              Know someone who deserves a free home upgrade? Nominate teachers, nurses, veterans, 
              or anyone who makes your community better.
            </p>
            
            <Button 
              onClick={() => setShowForm(true)}
              className="h-14 px-8 bg-white text-[#FF3B30] hover:bg-neutral-100 text-lg font-medium rounded-xl"
              data-testid="start-nomination"
            >
              <Heart className="mr-2 w-5 h-5" />
              Nominate Someone Special
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex overflow-x-auto gap-4 pb-4 -mx-4 px-4">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`flex-shrink-0 px-6 py-3 rounded-xl font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-[#FF3B30] text-white'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              All Nominations
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex-shrink-0 flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-colors ${
                  selectedCategory === cat.id
                    ? 'bg-[#FF3B30] text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                <cat.icon className="w-5 h-5" />
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Nominations List */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl h-32 animate-pulse" />
              ))}
            </div>
          ) : nominations.length > 0 ? (
            <div className="space-y-4" data-testid="nominations-list">
              {nominations.map((nomination, index) => {
                const category = categories.find(c => c.id === nomination.category);
                return (
                  <motion.div
                    key={nomination.nomination_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-2xl p-6 border border-neutral-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        nomination.category === 'teacher' ? 'bg-blue-100 text-blue-600' :
                        nomination.category === 'nurse' ? 'bg-pink-100 text-pink-600' :
                        nomination.category === 'veteran' ? 'bg-green-100 text-green-600' :
                        nomination.category === 'family_in_need' ? 'bg-amber-100 text-amber-600' :
                        'bg-purple-100 text-purple-600'
                      }`}>
                        {category && <category.icon className="w-6 h-6" />}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-lg text-neutral-900">{nomination.nominee_name}</h3>
                            <p className="text-sm text-neutral-500">{nomination.city} • {category?.name}</p>
                          </div>
                          <span className="bg-neutral-100 text-neutral-600 px-3 py-1 rounded-full text-sm font-medium">
                            {nomination.votes} votes
                          </span>
                        </div>
                        
                        <p className="mt-3 text-neutral-600 line-clamp-2">{nomination.reason}</p>
                        
                        <div className="mt-4 flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleVote(nomination.nomination_id)}
                            className="rounded-xl"
                          >
                            <ThumbsUp className="w-4 h-4 mr-1" />
                            Vote
                          </Button>
                          <Button variant="ghost" size="sm" className="rounded-xl">
                            <Share2 className="w-4 h-4 mr-1" />
                            Share
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-neutral-200">
              <Heart className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-neutral-600 mb-2">No Nominations Yet</h3>
              <p className="text-neutral-500 mb-6">Be the first to nominate someone special!</p>
              <Button onClick={() => setShowForm(true)} className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl">
                Start a Nomination
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Nomination Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
          >
            <h2 className="text-2xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              Nominate Someone Special
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="nomination-form">
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger className="mt-1 rounded-xl">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Their Name</Label>
                <Input
                  value={formData.nominee_name}
                  onChange={(e) => setFormData({ ...formData, nominee_name: e.target.value })}
                  placeholder="Who are you nominating?"
                  className="mt-1 rounded-xl"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Their Email (optional)</Label>
                  <Input
                    type="email"
                    value={formData.nominee_email}
                    onChange={(e) => setFormData({ ...formData, nominee_email: e.target.value })}
                    placeholder="email@example.com"
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label>Their Phone (optional)</Label>
                  <Input
                    value={formData.nominee_phone}
                    onChange={(e) => setFormData({ ...formData, nominee_phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>

              <div>
                <Label>Their City</Label>
                <Select value={formData.city} onValueChange={(v) => setFormData({ ...formData, city: v })}>
                  <SelectTrigger className="mt-1 rounded-xl">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="toronto">Toronto</SelectItem>
                    <SelectItem value="vancouver">Vancouver</SelectItem>
                    <SelectItem value="calgary">Calgary</SelectItem>
                    <SelectItem value="ottawa">Ottawa</SelectItem>
                    <SelectItem value="montreal">Montreal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Why do they deserve this?</Label>
                <Textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Tell us their story and why they deserve a free home upgrade..."
                  className="mt-1 rounded-xl"
                  rows={4}
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  className="flex-1 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || !formData.category || !formData.nominee_name || !formData.city || !formData.reason}
                  className="flex-1 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl"
                >
                  {submitting ? 'Submitting...' : 'Submit Nomination'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default NominateNeighbor;
